import React, { useEffect, useMemo, useState } from 'react';
import './Dropdown.css';
import LoadingOverlay from '../components/LoadingOverlay';
import PopupImportacaoEmMassa from '../Formulario/PopupImportacaoEmMassa';
import { getAuthToken } from '../utils/auth';
import BASE_URL from '../utils/api';

// Helper: listens for MobileFooter generic + event
function MobileAddListener({ onOpen }) {
  useEffect(() => {
    window.addEventListener('open-mobile-add-popup', onOpen);
    return () => window.removeEventListener('open-mobile-add-popup', onOpen);
  }, [onOpen]);
  return null;
}

// Helper API com fallbacks
const apiCall = async (endpoint) => {
  const urls = [
    `${BASE_URL}/${endpoint}`,
    `${BASE_URL}/${endpoint}`
  ];
  const headers = {};
  const token = getAuthToken();
  if (token) headers['Authorization'] = `Bearer ${token}`;
  let lastError = null;
  for (const url of urls) {
    try {
      const res = await fetch(url, { headers });
      if (res.ok) {
        const ct = res.headers.get('Content-Type') || '';
        if (ct.includes('application/json')) return await res.json();
        return await res.text();
      }
      lastError = `Status ${res.status} em ${url}`;
    } catch (e) {
      lastError = `${e.message} em ${url}`;
    }
  }
  throw new Error(`APIs indisponíveis para ${endpoint}. Último erro: ${lastError}`);
};

// Helper para POST/PUT/DELETE
const apiRequest = async (endpoint, method = 'POST', data = null) => {
  const urls = [
    `${BASE_URL}/${endpoint}`,
    `${BASE_URL}/${endpoint}`
  ];
  let lastError = null;
  for (const url of urls) {
    try {
      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: data ? JSON.stringify(data) : undefined
      });
      if (res.ok || (res.status >= 200 && res.status < 300)) {
        const ct = res.headers.get('Content-Type') || '';
        if (ct.includes('application/json')) return await res.json();
        return await res.text();
      }
      lastError = `Status ${res.status} em ${url}`;
    } catch (e) {
      lastError = `${e.message} em ${url}`;
    }
  }
  throw new Error(`Falha ao executar ${method} ${endpoint}. Último erro: ${lastError}`);
};

export default function EstoqueMaterialDropdown() {
  const [itens, setItens] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filtroCampo, setFiltroCampo] = useState('titulo');
  const [filtroValor, setFiltroValor] = useState('');
  const [dropdownAberto, setDropdownAberto] = useState(false);
  const [criarAberto, setCriarAberto] = useState(false);
  const [mostrarImportacaoMassa, setMostrarImportacaoMassa] = useState(false);
  const [modoEdicao, setModoEdicao] = useState(false);
  const [dadosEdicao, setDadosEdicao] = useState(null);
  const [fornecedores, setFornecedores] = useState([]);
  const [unidadesMedida, setUnidadesMedida] = useState([]);
  const [tiposEstoque, setTiposEstoque] = useState([]);
  const [usaEndpointEstoque, setUsaEndpointEstoque] = useState(true);
  const [novoMaterial, setNovoMaterial] = useState({
    titulo: '',
    tipo: '',
    tipoUnidade: '',
    unidadeMedidaId: '',
    quantidade: '',
    custoUnitario: '',
    fornecedorId: ''
  });

  useEffect(() => {
    const carregar = async () => {
      try {
        setLoading(true);
        // Tenta ler do endpoint EstoqueMaterial, faz fallback para MateriaPrima
        try {
          const dataEstoque = await apiCall('EstoqueMaterial');
          setUsaEndpointEstoque(true);
          setItens(Array.isArray(dataEstoque) ? dataEstoque : []);
          setError('');
        } catch (errEstoque) {
          const dataMateria = await apiCall('MateriaPrima');
          setUsaEndpointEstoque(false);
          setItens(Array.isArray(dataMateria) ? dataMateria : []);
          setError('');
        }
      } catch (e) {
        setError('Erro ao carregar estoque de materiais.');
        setItens([]);
      } finally {
        setLoading(false);
      }
    };
    carregar();
    // Carregar fornecedores para popular o select
    const carregarFornecedores = async () => {
      try {
        const data = await apiCall('Fornecedor');
        setFornecedores(Array.isArray(data) ? data : []);
      } catch (e) {
        setFornecedores([]);
      }
    };
    carregarFornecedores();

    // Carregar unidades de medida para o select
    const carregarUnidades = async () => {
      const tentativas = [
        'UnidadeMedida',
        'UnidadesMedida',
        'UnidadeMedidas',
        'Unidade',
        'Unidades'
      ];
      for (const ep of tentativas) {
        try {
          const data = await apiCall(ep);
          if (Array.isArray(data)) {
            setUnidadesMedida(data);
            return;
          }
        } catch (e) {
          // tenta próximo endpoint
        }
      }
      setUnidadesMedida([]);
    };
    carregarUnidades();

    // Carregar tipos de estoque (nova tabela de tipos)
    const carregarTiposEstoque = async () => {
      const tentativas = [
        'TipoEstoqueMaterial',
        'TipoEstoqueMateriais',
        'TipoMaterial',
        'TipoMateriaPrima',
        'Tipo',
        'Tipos'
      ];
      for (const ep of tentativas) {
        try {
          const data = await apiCall(ep);
          if (Array.isArray(data)) {
            setTiposEstoque(data);
            return;
          }
        } catch (e) {
          // tenta próximo endpoint
        }
      }
      setTiposEstoque([]);
    };
    carregarTiposEstoque();
  }, []);

  useEffect(() => {
    const handler = () => {
      setCriarAberto(false);
      setMostrarImportacaoMassa(false);
      setModoEdicao(false);
      setDadosEdicao(null);
      setDropdownAberto(false);
    };
    window.addEventListener('estoque-material-fechar-todos-popups', handler);
    return () => window.removeEventListener('estoque-material-fechar-todos-popups', handler);
  }, []);

  const handleCriarMaterial = async () => {
    if (!novoMaterial.titulo) {
      alert('O título é obrigatório');
      return;
    }
    if (!novoMaterial.unidadeMedidaId) {
      alert('Selecione a unidade de medida');
      return;
    }
    if (novoMaterial.quantidade === '' || Number(novoMaterial.quantidade) < 0) {
      alert('Informe uma quantidade válida (zero ou maior)');
      return;
    }
    if (novoMaterial.custoUnitario === '' || Number(novoMaterial.custoUnitario) < 0) {
      alert('Informe um custo unitário válido (zero ou maior)');
      return;
    }
    try {
      setLoading(true);
      // yield once so the LoadingOverlay renders before network requests start
      await Promise.resolve();
      // Deriva tipoUnidade a partir da unidade selecionada, se possível
      let tipoUnidadeFinal = novoMaterial.tipoUnidade || '';
      if (novoMaterial.unidadeMedidaId) {
        const u = unidadesMedida.find(x => (x.id || x.Id)?.toString() === novoMaterial.unidadeMedidaId?.toString());
        if (u) {
          const sigla = u.sigla || u.Sigla || u.codigo || u.Codigo || u.abreviacao || u.Abreviacao;
          const nome = u.nome || u.Nome || u.descricao || u.Descricao;
          tipoUnidadeFinal = sigla || nome || tipoUnidadeFinal;
        }
      }
      // Monta payload conforme endpoint disponível
      const payloadEstoque = {
        id: 0,
        fornecedorId: novoMaterial.fornecedorId ? parseInt(novoMaterial.fornecedorId, 10) : null,
        // envia o FK tipoId quando disponível para a nova tabela de tipos
        tipoId: novoMaterial.tipo ? parseInt(novoMaterial.tipo, 10) : null,
        // Mantém campo tipo por compatibilidade (tenta resolver label se possível)
        tipo: (() => {
          if (!novoMaterial.tipo) return '';
          const found = tiposEstoque.find(t => ((t.id || t.Id) + '') === (novoMaterial.tipo + ''));
          return found ? (found.nome || found.Nome || found.descricao || found.Descricao || '') : novoMaterial.tipo;
        })(),
        titulo: novoMaterial.titulo || '',
        tipoUnidade: tipoUnidadeFinal || '',
        unidadeMedidaId: novoMaterial.unidadeMedidaId ? parseInt(novoMaterial.unidadeMedidaId, 10) : null,
        tipoEstoqueMaterialId: novoMaterial.tipo ? parseInt(novoMaterial.tipo, 10) || null : null,
        quantidade: novoMaterial.quantidade ? parseFloat(novoMaterial.quantidade) : 0,
        custoUnitario: novoMaterial.custoUnitario ? parseFloat(novoMaterial.custoUnitario) : 0
      };

      const payloadMateriaPrima = {
        id: 0,
        descricao: novoMaterial.titulo || '',
        // matéria prima pode esperar um id ou string; enviar tipoId quando possível
        tipo: novoMaterial.tipo ? (isNaN(novoMaterial.tipo) ? novoMaterial.tipo : parseInt(novoMaterial.tipo, 10)) : null,
        valor: novoMaterial.custoUnitario ? parseFloat(novoMaterial.custoUnitario) : 0,
        fornecedorId: novoMaterial.fornecedorId ? parseInt(novoMaterial.fornecedorId, 10) : null,
        producaoId: 0,
        id_cliente: 0,
        dataExecucao: null
      };

      if (usaEndpointEstoque) {
        try {
          await apiRequest('EstoqueMaterial', 'POST', payloadEstoque);
        } catch (errEstoque) {
          // Fallback tenta MateriaPrima
          await apiRequest('MateriaPrima', 'POST', payloadMateriaPrima);
        }
      } else {
        // Ambiente antigo
        await apiRequest('MateriaPrima', 'POST', payloadMateriaPrima);
      }

      alert('Material adicionado com sucesso!');
      setCriarAberto(false);
  setNovoMaterial({ titulo: '', tipo: '', tipoUnidade: '', unidadeMedidaId: '', quantidade: '', custoUnitario: '', fornecedorId: '' });
      // Recarregar lista
      try {
        setLoading(true);
        await Promise.resolve();
        if (usaEndpointEstoque) {
          try {
            const data = await apiCall('EstoqueMaterial');
            setItens(Array.isArray(data) ? data : []);
          } catch (e) {
            const data = await apiCall('MateriaPrima');
            setItens(Array.isArray(data) ? data : []);
          }
        } else {
          const data = await apiCall('MateriaPrima');
          setItens(Array.isArray(data) ? data : []);
        }
      } finally {
        setLoading(false);
      }
    } catch (e) {
      console.error(e);
      alert('Erro ao adicionar material');
      setLoading(false);
    }
  };

  const handleEditar = (material) => {
    // Determina o tipoId a partir do material
    let tipoId = '';
    if (material.tipoEstoqueMaterialId || material.TipoEstoqueMaterialId || material.tipoId || material.TipoId) {
      tipoId = (material.tipoEstoqueMaterialId || material.TipoEstoqueMaterialId || material.tipoId || material.TipoId).toString();
    } else if (material.tipo && tiposEstoque.length) {
      // Tenta encontrar o tipo pelo nome
      const found = tiposEstoque.find(t => 
        (t.nome || t.Nome || t.descricao || t.Descricao || '') === material.tipo
      );
      if (found) {
        tipoId = (found.id || found.Id).toString();
      }
    }

    // Determina unidadeMedidaId
    let unidadeMedidaId = '';
    if (material.unidadeMedidaId || material.UnidadeMedidaId) {
      unidadeMedidaId = (material.unidadeMedidaId || material.UnidadeMedidaId).toString();
    } else if (material.tipoUnidade && unidadesMedida.length) {
      const found = unidadesMedida.find(u => {
        const sigla = u.sigla || u.Sigla || u.codigo || u.Codigo || u.abreviacao || u.Abreviacao;
        return sigla === material.tipoUnidade;
      });
      if (found) {
        unidadeMedidaId = (found.id || found.Id).toString();
      }
    }

    setDadosEdicao(material);
    setNovoMaterial({
      titulo: material.titulo || material.descricao || '',
      tipo: tipoId,
      tipoUnidade: material.tipoUnidade || '',
      unidadeMedidaId: unidadeMedidaId,
      quantidade: material.quantidade?.toString() || '',
      custoUnitario: (material.custoUnitario || material.valor)?.toString() || '',
      fornecedorId: (material.fornecedorId || material.FornecedorId)?.toString() || ''
    });
    setModoEdicao(true);
    setCriarAberto(true);
  };

  const handleAtualizarMaterial = async () => {
    if (!novoMaterial.titulo) {
      alert('O título é obrigatório');
      return;
    }
    if (!novoMaterial.unidadeMedidaId) {
      alert('Selecione a unidade de medida');
      return;
    }
    if (novoMaterial.quantidade === '' || Number(novoMaterial.quantidade) < 0) {
      alert('Informe uma quantidade válida (zero ou maior)');
      return;
    }
    if (novoMaterial.custoUnitario === '' || Number(novoMaterial.custoUnitario) < 0) {
      alert('Informe um custo unitário válido (zero ou maior)');
      return;
    }
    try {
      setLoading(true);
      await Promise.resolve();

      let tipoUnidadeFinal = novoMaterial.tipoUnidade || '';
      if (novoMaterial.unidadeMedidaId) {
        const u = unidadesMedida.find(x => (x.id || x.Id)?.toString() === novoMaterial.unidadeMedidaId?.toString());
        if (u) {
          const sigla = u.sigla || u.Sigla || u.codigo || u.Codigo || u.abreviacao || u.Abreviacao;
          const nome = u.nome || u.Nome || u.descricao || u.Descricao;
          tipoUnidadeFinal = sigla || nome || tipoUnidadeFinal;
        }
      }

      const payloadEstoque = {
        id: dadosEdicao.id,
        fornecedorId: novoMaterial.fornecedorId ? parseInt(novoMaterial.fornecedorId, 10) : null,
        tipoId: novoMaterial.tipo ? parseInt(novoMaterial.tipo, 10) : null,
        tipo: (() => {
          if (!novoMaterial.tipo) return '';
          const found = tiposEstoque.find(t => ((t.id || t.Id) + '') === (novoMaterial.tipo + ''));
          return found ? (found.nome || found.Nome || found.descricao || found.Descricao || '') : novoMaterial.tipo;
        })(),
        titulo: novoMaterial.titulo || '',
        tipoUnidade: tipoUnidadeFinal || '',
        unidadeMedidaId: novoMaterial.unidadeMedidaId ? parseInt(novoMaterial.unidadeMedidaId, 10) : null,
        tipoEstoqueMaterialId: novoMaterial.tipo ? parseInt(novoMaterial.tipo, 10) || null : null,
        quantidade: novoMaterial.quantidade ? parseFloat(novoMaterial.quantidade) : 0,
        custoUnitario: novoMaterial.custoUnitario ? parseFloat(novoMaterial.custoUnitario) : 0
      };

      const payloadMateriaPrima = {
        id: dadosEdicao.id,
        descricao: novoMaterial.titulo || '',
        tipo: novoMaterial.tipo ? (isNaN(novoMaterial.tipo) ? novoMaterial.tipo : parseInt(novoMaterial.tipo, 10)) : null,
        valor: novoMaterial.custoUnitario ? parseFloat(novoMaterial.custoUnitario) : 0,
        fornecedorId: novoMaterial.fornecedorId ? parseInt(novoMaterial.fornecedorId, 10) : null,
        producaoId: dadosEdicao.producaoId || 0,
        id_cliente: dadosEdicao.id_cliente || 0,
        dataExecucao: dadosEdicao.dataExecucao || null
      };

      if (usaEndpointEstoque) {
        try {
          await apiRequest(`EstoqueMaterial/${dadosEdicao.id}`, 'PUT', payloadEstoque);
        } catch (errEstoque) {
          await apiRequest(`MateriaPrima/${dadosEdicao.id}`, 'PUT', payloadMateriaPrima);
        }
      } else {
        await apiRequest(`MateriaPrima/${dadosEdicao.id}`, 'PUT', payloadMateriaPrima);
      }

      alert('Material atualizado com sucesso!');
      setCriarAberto(false);
      setModoEdicao(false);
      setDadosEdicao(null);
      setNovoMaterial({ titulo: '', tipo: '', tipoUnidade: '', unidadeMedidaId: '', quantidade: '', custoUnitario: '', fornecedorId: '' });
      
      // Recarregar lista
      try {
        setLoading(true);
        await Promise.resolve();
        if (usaEndpointEstoque) {
          try {
            const data = await apiCall('EstoqueMaterial');
            setItens(Array.isArray(data) ? data : []);
          } catch (e) {
            const data = await apiCall('MateriaPrima');
            setItens(Array.isArray(data) ? data : []);
          }
        } else {
          const data = await apiCall('MateriaPrima');
          setItens(Array.isArray(data) ? data : []);
        }
      } finally {
        setLoading(false);
      }
    } catch (e) {
      console.error(e);
      alert('Erro ao atualizar material');
      setLoading(false);
    }
  };

  const handleExcluirMaterial = async (materialId) => {
    if (!window.confirm('Tem certeza que deseja excluir este material?')) {
      return;
    }
    try {
      setLoading(true);
      await Promise.resolve();

      if (usaEndpointEstoque) {
        try {
          await apiRequest(`EstoqueMaterial/${materialId}`, 'DELETE');
        } catch (errEstoque) {
          await apiRequest(`MateriaPrima/${materialId}`, 'DELETE');
        }
      } else {
        await apiRequest(`MateriaPrima/${materialId}`, 'DELETE');
      }

      alert('Material excluído com sucesso!');
      
      // Recarregar lista
      try {
        setLoading(true);
        await Promise.resolve();
        if (usaEndpointEstoque) {
          try {
            const data = await apiCall('EstoqueMaterial');
            setItens(Array.isArray(data) ? data : []);
          } catch (e) {
            const data = await apiCall('MateriaPrima');
            setItens(Array.isArray(data) ? data : []);
          }
        } else {
          const data = await apiCall('MateriaPrima');
          setItens(Array.isArray(data) ? data : []);
        }
      } finally {
        setLoading(false);
      }
    } catch (e) {
      console.error(e);
      alert('Erro ao excluir material');
      setLoading(false);
    }
  };

  const itensFiltrados = useMemo(() => {
    const val = filtroValor.trim().toLowerCase();
    if (!val) return itens;
    return itens.filter((m) => {
      // Normaliza campos vindos de diferentes endpoints
      const titulo = (m.titulo || m.descricao || '').toLowerCase();
      const tipo = (m.tipo || '').toLowerCase();
      const fornecedorNome = (m.fornecedorNome || m.nomeFornecedor || m.NomeFornecedor || m.fornecedor?.nome || '').toLowerCase();
      if (filtroCampo === 'titulo') return titulo.includes(val);
      if (filtroCampo === 'tipo') return tipo.includes(val);
      if (filtroCampo === 'fornecedor') return fornecedorNome.includes(val);
      return true;
    });
  }, [filtroCampo, filtroValor, itens]);

  // Ordena tipos colocando "Outros" sempre no final
  const sortedTiposEstoque = useMemo(() => {
    if (!Array.isArray(tiposEstoque) || tiposEstoque.length === 0) return [];
    const arr = [...tiposEstoque];
    arr.sort((a, b) => {
      const la = (a.nome || a.Nome || a.descricao || a.Descricao || '').toString().toLowerCase();
      const lb = (b.nome || b.Nome || b.descricao || b.Descricao || '').toString().toLowerCase();
      const isAOutros = la === 'outros' || la === 'outro';
      const isBOutros = lb === 'outros' || lb === 'outro';
      if (isAOutros && !isBOutros) return 1;
      if (!isAOutros && isBOutros) return -1;
      return la.localeCompare(lb);
    });
    return arr;
  }, [tiposEstoque]);

  const Campo = ({ label, value, minWidth = 120, className = '' }) => (
    <div className={className} style={{ display: 'flex', flexDirection: 'column', minWidth }}>
      <label style={{ fontWeight: 600, marginBottom: 4, color: '#666' }}>{label}</label>
      <span>{value ?? '—'}</span>
    </div>
  );

  return (
    <div className="container-geral fundo-branco" style={{ minHeight: '100vh', background: '#f0f0f0', padding: 20 }}>
      <h2 style={{ textAlign: 'center', color: 'rgb(235, 133, 0)', marginBottom: 16 }}>Estoque de Materiais</h2>

      <MobileAddListener onOpen={() => setCriarAberto(true)} />

      {/* Ações */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, justifyContent: 'center', marginBottom: 16 }}>
        <button
          onClick={() => setCriarAberto(true)}
          style={{
            padding: '12px 24px',
            backgroundColor: 'rgb(235, 133, 0)',
            color: 'white',
            border: 'none',
            borderRadius: 6,
            cursor: 'pointer',
            fontWeight: 600,
            fontSize: '16px',
            boxShadow: '0 2px 6px rgba(0,0,0,0.15)'
          }}
        >
          Adicionar Material
        </button>
        <button
          onClick={() => setMostrarImportacaoMassa(true)}
          style={{ padding: '12px 24px', backgroundColor: '#2e7d32', color: 'white', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 600, fontSize: '16px', boxShadow: '0 2px 6px rgba(0,0,0,0.15)' }}
        >
          Cadastro em Massa
        </button>
      </div>

      {/* Filtro */}
      <div className="filtro-container">
        <div className="filtro-wrapper">
          <input
            type="text"
            className="filtro-input"
            placeholder={`Filtrar por ${
              filtroCampo === 'titulo' ? 'Título' : filtroCampo === 'tipo' ? 'Tipo' : 'Fornecedor'
            }...`}
            value={filtroValor}
            onChange={(e) => setFiltroValor(e.target.value)}
          />
          <div className="dropdown-wrapper">
            <button className="dropdown-button" onClick={() => setDropdownAberto(!dropdownAberto)}>
              {filtroCampo === 'titulo' ? 'Título' : filtroCampo === 'tipo' ? 'Tipo' : 'Fornecedor'}
              <span className="dropdown-arrow">▼</span>
            </button>
            {dropdownAberto && (
              <div className="dropdown-menu">
                <div className="dropdown-item" onClick={() => { setFiltroCampo('titulo'); setDropdownAberto(false); }}>Título</div>
                <div className="dropdown-item" onClick={() => { setFiltroCampo('tipo'); setDropdownAberto(false); }}>Tipo</div>
                <div className="dropdown-item" onClick={() => { setFiltroCampo('fornecedor'); setDropdownAberto(false); }}>Fornecedor</div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Estados */}
      {loading && (<div style={{ textAlign: 'center', padding: 40, color: '#666' }}>Carregando...</div>)}
      {error && (<div style={{ textAlign: 'center', padding: 20, color: '#b00020', background: '#fdecea', borderRadius: 6, margin: '20px 0' }}>{error}</div>)}

      {/* Lista */}
      {!loading && !error && (
        <div className="estoque-material-cards-container">
          {(itensFiltrados?.length || 0) === 0 ? (
            <div style={{ textAlign: 'center', padding: 40, color: '#666', background: 'white', marginTop: 10 }}>
              {filtroValor ? 'Nenhum material encontrado para o filtro' : 'Nenhum material cadastrado'}
            </div>
          ) : (
            itensFiltrados.map((m, idx) => (
              <div className="estoque-material-card" key={m.id || idx}>
                <div className="estoque-material-card-content">
                  <div className="estoque-material-card-header">
                    Material #{m.id}
                  </div>
                  <div className="estoque-material-card-fields">
                    <div className="estoque-field estoque-field-id">
                      <span className="estoque-field-label">ID:</span>
                      <span className="estoque-field-value">{m.id}</span>
                    </div>
                    <div className="estoque-field estoque-field-titulo estoque-field-full">
                      <span className="estoque-field-label">Título:</span>
                      <span className="estoque-field-value">{m.titulo || m.descricao || '—'}</span>
                    </div>
                    <div className="estoque-field estoque-field-tipo">
                      <span className="estoque-field-label">Tipo:</span>
                      <span className="estoque-field-value">{(() => {
                        if (m.tipoEstoqueMaterialNome || m.TipoEstoqueMaterialNome) return m.tipoEstoqueMaterialNome || m.TipoEstoqueMaterialNome;
                        if (m.tipo) return m.tipo;
                        const tipoId = m.tipoEstoqueMaterialId || m.TipoEstoqueMaterialId || m.tipoId || m.TipoId;
                        if (tipoId && tiposEstoque.length) {
                          const found = tiposEstoque.find(t => (t.id || t.Id) == tipoId);
                          return found ? (found.nome || found.Nome || found.descricao || found.Descricao || `${found.id || found.Id}`) : (m.tipo || '—');
                        }
                        return m.tipo || '—';
                      })()}</span>
                    </div>
                    <div className="estoque-field estoque-field-unidade">
                      <span className="estoque-field-label">Unidade:</span>
                      <span className="estoque-field-value">{m.unidadeMedida?.sigla || m.UnidadeMedida?.Sigla || m.tipoUnidade || '—'}</span>
                    </div>
                    <div className="estoque-field estoque-field-quantidade">
                      <span className="estoque-field-label">Quantidade:</span>
                      <span className="estoque-field-value">{m.quantidade != null ? Number(m.quantidade).toLocaleString('pt-BR') : '—'}</span>
                    </div>
                    <div className="estoque-field estoque-field-custo">
                      <span className="estoque-field-label">Custo Unitário:</span>
                      <span className="estoque-field-value">{
                        m.custoUnitario != null
                          ? Number(m.custoUnitario).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
                          : (m.valor != null ? Number(m.valor).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }) : '—')
                      }</span>
                    </div>
                    <div className="estoque-field estoque-field-fornecedor estoque-field-full">
                      <span className="estoque-field-label">Fornecedor:</span>
                      <span className="estoque-field-value">{m.fornecedorNome || m.nomeFornecedor || m.NomeFornecedor || m.fornecedor?.nome || '—'}</span>
                    </div>
                  </div>
                </div>
                <div className="estoque-material-card-actions">
                  <button className="btn-editar-estoque" onClick={() => handleEditar(m)}>
                    Editar
                  </button>
                  <button className="btn-excluir-estoque" onClick={() => handleExcluirMaterial(m.id)}>
                    Excluir
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* Modal Criar Material */}
      {criarAberto && (
        <div className="modal-overlay" onClick={() => { setCriarAberto(false); setModoEdicao(false); setDadosEdicao(null); }} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 }}>
          <div className="modal formulario-estoque-material-modal" onClick={(e) => e.stopPropagation()} style={{ width: '92%', maxWidth: 700, background: '#fff', borderRadius: 12, padding: 20, maxHeight: '85vh', overflowY: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <h3 style={{ margin: 0, color: 'rgb(235, 133, 0)' }}>{modoEdicao ? 'Editar Material' : 'Adicionar Material'}</h3>
              <button onClick={() => { setCriarAberto(false); setModoEdicao(false); setDadosEdicao(null); }} aria-label="Fechar" title="Fechar" style={{ backgroundColor: 'rgb(235, 133, 0)', color: '#fff', border: 'none', borderRadius: '50%', width: 36, height: 36, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, cursor: 'pointer', boxShadow: '0 2px 6px rgba(0,0,0,0.15)' }}>×</button>
            </div>
            <div className="formulario-estoque-material-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
              <div className="form-field-estoque form-field-estoque-full" style={{ display: 'flex', flexDirection: 'column', gridColumn: 'span 2' }}>
                <label style={{ fontSize: 13, marginBottom: 6, fontWeight: 600 }}>Título *</label>
                <input
                  type="text"
                  value={novoMaterial.titulo}
                  onChange={(e) => setNovoMaterial({ ...novoMaterial, titulo: e.target.value })}
                  style={{ padding: 10, borderRadius: 6, border: '1px solid #ddd' }}
                  placeholder="Nome do material"
                />
              </div>
              <div className="form-field-estoque" style={{ display: 'flex', flexDirection: 'column' }}>
                <label style={{ fontSize: 13, marginBottom: 6, fontWeight: 600 }}>Tipo</label>
                <select
                  value={novoMaterial.tipo}
                  onChange={(e) => setNovoMaterial({ ...novoMaterial, tipo: e.target.value })}
                  style={{ padding: 10, borderRadius: 6, border: '1px solid #ddd' }}
                >
                  <option value="">Selecione um tipo</option>
                  {tiposEstoque.map(t => {
                    const id = t.id || t.Id;
                    const label = t.nome || t.Nome || t.descricao || t.Descricao || t.tipo || t.Tipo || `Tipo ${id}`;
                    return <option key={id ?? label} value={id}>{label}</option>;
                  })}
                </select>
              </div>
              <div className="form-field-estoque" style={{ display: 'flex', flexDirection: 'column' }}>
                <label style={{ fontSize: 13, marginBottom: 6, fontWeight: 600 }}>Unidade de Medida</label>
                <select
                  value={novoMaterial.unidadeMedidaId}
                  onChange={(e) => setNovoMaterial({ ...novoMaterial, unidadeMedidaId: e.target.value })}
                  style={{ padding: 10, borderRadius: 6, border: '1px solid #ddd' }}
                >
                  <option value="">Selecione</option>
                  {unidadesMedida.map(u => {
                    const id = u.id || u.Id;
                    const sigla = u.sigla || u.Sigla || u.codigo || u.Codigo || u.abreviacao || u.Abreviacao || '';
                    const nome = u.nome || u.Nome || u.descricao || u.Descricao || '';
                    const label = [sigla, nome].filter(Boolean).join(' - ') || `Unidade ${id}`;
                    return (
                      <option key={id ?? label} value={id}>{label}</option>
                    );
                  })}
                </select>
              </div>
              <div className="form-field-estoque" style={{ display: 'flex', flexDirection: 'column' }}>
                <label style={{ fontSize: 13, marginBottom: 6, fontWeight: 600 }}>Quantidade</label>
                <input
                  type="number"
                  step="0.01"
                  value={novoMaterial.quantidade}
                  onChange={(e) => setNovoMaterial({ ...novoMaterial, quantidade: e.target.value })}
                  style={{ padding: 10, borderRadius: 6, border: '1px solid #ddd' }}
                  placeholder="0"
                />
              </div>
              <div className="form-field-estoque" style={{ display: 'flex', flexDirection: 'column' }}>
                <label style={{ fontSize: 13, marginBottom: 6, fontWeight: 600 }}>Custo Unitário (R$)</label>
                <input
                  type="number"
                  step="0.01"
                  value={novoMaterial.custoUnitario}
                  onChange={(e) => setNovoMaterial({ ...novoMaterial, custoUnitario: e.target.value })}
                  style={{ padding: 10, borderRadius: 6, border: '1px solid #ddd' }}
                  placeholder="0.00"
                />
              </div>
              <div className="form-field-estoque" style={{ display: 'flex', flexDirection: 'column' }}>
                <label style={{ fontSize: 13, marginBottom: 6, fontWeight: 600 }}>Fornecedor</label>
                <select
                  value={novoMaterial.fornecedorId}
                  onChange={(e) => setNovoMaterial({ ...novoMaterial, fornecedorId: e.target.value })}
                  style={{ padding: 10, borderRadius: 6, border: '1px solid #ddd' }}
                >
                  <option value="">Selecione um fornecedor</option>
                  {fornecedores.map(f => (
                    <option key={f.id || f.Id} value={f.id || f.Id}>{f.nomeFornecedor || f.NomeFornecedor || f.nome || f.Nome}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="form-buttons-container-estoque" style={{ marginTop: 20, display: 'flex', gap: 10, justifyContent: 'center' }}>
              <button
                className="btn-salvar-estoque"
                onClick={modoEdicao ? handleAtualizarMaterial : handleCriarMaterial}
                disabled={loading}
                style={{ padding: '12px 30px', backgroundColor: 'rgb(235, 133, 0)', color: 'white', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 600 }}
              >
                {modoEdicao ? 'Atualizar' : 'Adicionar'}
              </button>
              <button
                className="btn-cancelar-estoque"
                onClick={() => { setCriarAberto(false); setModoEdicao(false); setDadosEdicao(null); }}
                style={{ padding: '12px 30px', backgroundColor: '#f44336', color: 'white', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 600 }}
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}

      {mostrarImportacaoMassa && (
        <PopupImportacaoEmMassa
          titulo="Materiais"
          nomeArquivo="template_importacao_materiais"
          nomeAba="Materiais"
          colunas={['Titulo', 'UnidadeMedidaId', 'Quantidade', 'CustoUnitario', 'FornecedorId', 'TipoEstoqueMaterialId']}
          camposObrigatorios={['Titulo', 'UnidadeMedidaId', 'Quantidade', 'CustoUnitario']}
          exemplos={[{ Titulo: 'Parafuso 10mm', UnidadeMedidaId: 1, Quantidade: 100, CustoUnitario: 0.5, FornecedorId: '', TipoEstoqueMaterialId: '' }]}
          instrucoes={['Preencha uma linha para cada material.', 'UnidadeMedidaId é obrigatório e deve ser um ID já cadastrado.', 'FornecedorId e TipoEstoqueMaterialId são opcionais e devem ser IDs já cadastrados.']}
          endpoint="EstoqueMaterial"
          criarPayload={(linha) => ({
            titulo: String(linha.Titulo).trim(),
            unidadeMedidaId: Number(linha.UnidadeMedidaId),
            quantidade: Number(String(linha.Quantidade).replace(',', '.')),
            custoUnitario: Number(String(linha.CustoUnitario).replace(',', '.')),
            fornecedorId: linha.FornecedorId === '' ? null : Number(linha.FornecedorId),
            tipoEstoqueMaterialId: linha.TipoEstoqueMaterialId === '' ? null : Number(linha.TipoEstoqueMaterialId)
          })}
          onFechar={() => setMostrarImportacaoMassa(false)}
          onSucesso={() => window.location.reload()}
        />
      )}

      <LoadingOverlay isLoading={loading} message="Processando..." />
    </div>
  );
}
